//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useRef, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Jitsi from 'react-jitsi';
import { useMediaQuery, useTheme } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AFITD_JITSI_URL } from '../../system/constants';
import { AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { INTERVIEW_STATUS_CONFIRMED } from '../../common/constants';
import { getFullNameByUser } from '../../common/textHelper';
import { getJitsiConfig, getJitsiJwt } from '../../common/jitsiHelper';
import { fixJitsiRoomName, getTempJitsiConfig } from '../../common/jitsiHelper';
import { getUserInfoByEmployerId } from '../../common/employerHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdTypography from '../../common/elemental/AfitdTypography';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import _get from 'lodash/get';
import subMinutes from 'date-fns/subMinutes';
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {},
    buttonLabel: {},
    containedDefault: {},
    containedPrimary: {},
    containedSecondary: {},
    modalDiv: {
        padding: '16px',
        border: '1px solid #1D8A8B',
        borderRadius: '4px',
    },

    typeDiv: {
        marginBottom: '50px',
    },
    flex: {
        display: 'flex',
        justifyContent: 'space-between',
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        otherProps,
        jitsiDevMode,
        isEmployer,
        jitsiToken,
        setJitsiToken,
        videoRef,
        theStream,
        setTheStream,
        interviewUserDisplayName,
        interviewUserEmail,
        interviewUserProfilePicUrl,
        sm,
    } = params;
    const { id, title, companyName, externalInterviewLink } = props;
    const { ...otherClasses } = classes;

    const jitsiRoomName = encodeURIComponent(fixJitsiRoomName(`${title}${id}`));
    const subject = `${title} at ${companyName}`;

    const jitsiContainerStyle = { width: '90vw', minHeight: '400px', height: sm ? '530px' : '400px', maxWidth: '800px' };

    let defns = {
        button: {
            color: 'primary',
            ...otherProps,
            onClick: handleOnClick.bind(
                null,
                externalInterviewLink,
                setJitsiToken,
                videoRef,
                setTheStream,
                interviewUserDisplayName,
                interviewUserEmail,
                interviewUserProfilePicUrl,
            ),
        },
        closeButton: {
            onClick: handleCloseButtonOnClick.bind(null, setJitsiToken, videoRef, theStream),
        },
        type: {
            variant: 'caption',
        },
        jitsi: jitsiDevMode
            ? {
                  containerStyle: jitsiContainerStyle,
                  config: getTempJitsiConfig({ isEmployer, subject }),
                  roomName: jitsiRoomName,
                  displayName: interviewUserDisplayName,
                  // password,
                  // loadingComponent: Loader,
                  // onAPILoad: JitsiMeetAPI => console.log('Good Morning everyone!'
              }
            : {
                  domain: AFITD_JITSI_URL,
                  jwt: jitsiToken,
                  containerStyle: jitsiContainerStyle,
                  config: getJitsiConfig({ isEmployer, subject }),
                  roomName: jitsiRoomName,
                  displayName: interviewUserDisplayName,
              },
    };

    setClassNameInDefns(defns, otherClasses);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const handlePermissionGranted = (videoRef, setTheStream, stream) => {
    setTheStream(stream);
    videoRef.current.srcObject = stream;
};

const handlePermissionDenied = () => {
    console.error('@@@@ JoinInterviewButton handlePermissionDenied');
};

// const handleOnClick = async (setJitsiToken, videoRef, setTheStream, name, email, profilePicUrl) => {
//     if (setJitsiToken) {
//         const jitsiTokenData = await getJitsiJwt({ name, email, avatar: profilePicUrl });
//
//         setJitsiToken(jitsiTokenData.token);
//
//         const constraints = {
//             video: true,
//         };
//         navigator.mediaDevices.getUserMedia(constraints).then(handlePermissionGranted.bind(null, videoRef, setTheStream)).catch(handlePermissionDenied);
//     }
// };

const handleOnClick = async (externalInterviewLink, setJitsiToken, videoRef, setTheStream, name, email, profilePicUrl) => {
    if (externalInterviewLink) {
        window.open(externalInterviewLink, '_blank');
    } else if (setJitsiToken) {
        const jitsiTokenData = await getJitsiJwt({ name, email, avatar: profilePicUrl });

        setJitsiToken(jitsiTokenData.token);

        const constraints = {
            video: true,
        };
        navigator.mediaDevices.getUserMedia(constraints).then(handlePermissionGranted.bind(null, videoRef, setTheStream)).catch(handlePermissionDenied);
    }
};

const handleCloseButtonOnClick = (setJitsiToken, videoRef, theStream) => {
    if (setJitsiToken) {
        setJitsiToken(null);
        // videoRef.current.pause();

        if (theStream) {
            const tracks = theStream.getTracks();

            tracks.forEach(function (track) {
                track.stop();
            });
        }
    }
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const JoinInterviewButton = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const theme = useTheme();

    const appContext = useContext(AppContext);
    const { employerId, currentEmployer, currentUser, sys } = appContext;
    const videoRef = useRef();
    const sm = useMediaQuery(theme.breakpoints.down('sm'));

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { isHidden, id, title, startIcon, label, startDt, endDt, interviewStatus, onClick, externalInterviewLink, ...otherProps } = props;

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const jitsiDevMode = sys?.jitsiDevMode;
    const isEmployer = !!currentEmployer;
    const userInfo = isEmployer ? getUserInfoByEmployerId({ currentUser, employerId }) : currentUser;
    const interviewUserDisplayName = getFullNameByUser(userInfo);
    const interviewUserEmail = userInfo?.email;
    const interviewUserProfilePicUrl = userInfo?.profilePicUrl;
    const threeMinsBeforeStart = subMinutes(startDt, 3);
    const currentDate = Date.now();

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [msToStart, setMsToStart] = useState(differenceInMilliseconds(threeMinsBeforeStart, currentDate));
    // const [msToEnd, setMsToEnd] = useState(differenceInMilliseconds(endDt, currentDate));
    // const [openDialog, setOpenDialog] = useState(false);
    const [jitsiToken, setJitsiToken] = useState(null);
    const [theStream, setTheStream] = useState(null);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const dialogOpen = !!jitsiToken;

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        if (msToStart > 0) {
            const timer1 = setTimeout(() => {
                setMsToStart(-1);
            }, msToStart);
            return () => clearTimeout(timer1);
        }
        // if (msToEnd > 0) {
        //     const timer2 = setTimeout(() => {
        //         setMsToEnd(-1);
        //     }, msToEnd);
        //     return () => clearTimeout(timer2);
        // }
    });

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        otherProps,
        jitsiDevMode,
        isEmployer,
        jitsiToken,
        setJitsiToken,
        videoRef,
        theStream,
        setTheStream,
        interviewUserDisplayName,
        interviewUserEmail,
        interviewUserProfilePicUrl,
        sm,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    if (isHidden || interviewStatus !== INTERVIEW_STATUS_CONFIRMED || msToStart > 0) {
        onClick(false);
        return null;
    } else {
        onClick(true);
        return (
            <div>
                <AfitdButton {...defns.button}>Join Interview</AfitdButton>
                <Dialog aria-labelledby="decline-dialog" aria-describedby="decline-dialog" open={dialogOpen} maxWidth="lg" fullScreen={sm}>
                    <DialogTitle id="alert-dialog-title">
                        <div {...defns.flex}>
                            <AfitdTypography>Live Interview in Session</AfitdTypography>
                            {sm && <CloseIcon {...defns.closeButton} />}
                        </div>
                    </DialogTitle>
                    <DialogContent>
                        <Jitsi {...defns.jitsi} />
                    </DialogContent>
                    <DialogActions>
                        <AfitdButton {...defns.closeButton}>Close</AfitdButton>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
};

export default JoinInterviewButton;
